/**
 * Notification Context
 * Provides Ant Design message API that supports dynamic theme and i18n
 */
import { createContext, useContext, useCallback, useMemo, ReactNode } from 'react';
import { App } from 'antd';
import { useTranslation } from 'react-i18next';
import type { MessageInstance } from 'antd/es/message/interface';

type MessageType = 'success' | 'info' | 'warning' | 'error';

interface NotificationContextValue {
  message: MessageInstance;
  showTranslated: (
    type: MessageType,
    key: string,
    options?: Record<string, unknown>
  ) => void;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context.message;
};

export const useTranslatedMessage = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useTranslatedMessage must be used within NotificationProvider');
  }
  return context.showTranslated;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { message } = App.useApp();
  const { t } = useTranslation('common');

  const showTranslated = useCallback(
    (type: MessageType, key: string, options?: Record<string, unknown>) => {
      const content = t(key, options);
      message[type](content);
    },
    [message, t]
  );

  const value = useMemo(
    () => ({ message, showTranslated }),
    [message, showTranslated]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
